import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { PIT, START_LINE_X, TRACK, TRUCK_CHECKPOINTS, TRUCK_GRID, TRUCK_MUD, TRUCK_RAMPS } from "@townsy/world";
import { ctx2d, mulberry32 } from "../../scene/core/materials";

/** The dirt ground covers the whole track (north of the trackside barrier, inside the outer walls). */
export const DIRT_RECT = { minX: TRACK.outerMinX, maxX: TRACK.outerMaxX, minZ: PIT.trackEdgeZ, maxZ: TRACK.outerMaxZ } as const;
export const DIRT_TEXTURE = { width: 1536, height: 928 } as const;

/** Centre-line points of the circuit (tyre marks follow it). */
const RACING_LINE: readonly [number, number][] = [
  [-36, -35],
  [40, -35],
  [68, -31],
  [74, -10],
  [78, 15],
  [74, 40],
  [60, 48],
  [20, 48],
  [-20, 48],
  [-66, 48],
  [-74, 36],
  [-74, 10],
  [-74, -20],
  [-66, -35],
  [-36, -35],
];

/**
 * Paint the arena's dirt: base colour with clods and ruts, tyre marks along the racing line, a darker wet edge round
 * the mud, the chequered start / finish line, the numbered starting grid, white checkpoint lines and the yellow landing
 * zones after each jump. World x maps to the canvas width, world z (north) to the top of the canvas.
 */
export function paintDirt(tex: DynamicTexture): void {
  const W = DIRT_TEXTURE.width;
  const H = DIRT_TEXTURE.height;
  const r = DIRT_RECT;
  const sx = W / (r.maxX - r.minX);
  const sz = H / (r.maxZ - r.minZ);
  const px = (x: number) => (x - r.minX) * sx;
  const py = (z: number) => (r.maxZ - z) * sz;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(7331);

  ctx.fillStyle = "#9A6A3E";
  ctx.fillRect(0, 0, W, H);
  // Clods and patches.
  const tones = ["#8C5E35", "#A87748", "#93643A", "#B0834F", "#7E5430"];
  for (let i = 0; i < 9000; i++) {
    ctx.globalAlpha = 0.25 + rnd() * 0.45;
    ctx.fillStyle = tones[Math.floor(rnd() * tones.length)]!;
    const w = 1 + rnd() * 5;
    ctx.beginPath();
    ctx.ellipse(rnd() * W, rnd() * H, w, w * (0.5 + rnd() * 0.6), rnd() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Tyre marks: a few dark ruts weaving around the racing line.
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (let k = 0; k < 9; k++) {
    const off = (k - 4) * 1.4 + (rnd() - 0.5);
    ctx.strokeStyle = k % 3 === 0 ? "rgba(60,38,20,0.38)" : "rgba(70,45,24,0.24)";
    ctx.lineWidth = (0.35 + rnd() * 0.25) * sx;
    ctx.beginPath();
    RACING_LINE.forEach(([x, z], i) => {
      const wobble = Math.sin(i * 1.7 + k) * 0.8;
      const X = px(x + (Math.abs(z) > 30 ? 0 : off + wobble));
      const Y = py(z + (Math.abs(z) > 30 ? off + wobble : 0));
      if (i === 0) ctx.moveTo(X, Y);
      else ctx.lineTo(X, Y);
    });
    ctx.stroke();
  }

  // Wet edge round the mud pit (the mud itself is its own glossy mesh).
  for (const m of TRUCK_MUD) {
    ctx.fillStyle = "rgba(70,44,22,0.55)";
    const pad = 1.2;
    ctx.fillRect(px(m.rect.minX - pad), py(m.rect.maxZ + pad), (m.rect.maxX - m.rect.minX + 2 * pad) * sx, (m.rect.maxZ - m.rect.minZ + 2 * pad) * sz);
  }

  // Landing zones: yellow chevrons pointing along the jump.
  for (const ramp of TRUCK_RAMPS) {
    const l = ramp.landing;
    if (!l) continue;
    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = "#F4C430";
    ctx.lineWidth = 0.45 * sx;
    const along = ramp.dir.x !== 0;
    const len = along ? l.maxX - l.minX : l.maxZ - l.minZ;
    const across = along ? l.maxZ - l.minZ : l.maxX - l.minX;
    for (let d = 1.5; d < len - 0.5; d += 3) {
      ctx.beginPath();
      for (const side of [-1, 1]) {
        const a0 = side * (across / 2 - 1);
        // Chevron apex points along dir.
        const base = ramp.dir.x > 0 || ramp.dir.z > 0 ? d : len - d;
        const tip = base + (ramp.dir.x > 0 || ramp.dir.z > 0 ? 1.4 : -1.4);
        const p0 = along ? [l.minX + base, (l.minZ + l.maxZ) / 2 + a0] : [(l.minX + l.maxX) / 2 + a0, l.minZ + base];
        const p1 = along ? [l.minX + tip, (l.minZ + l.maxZ) / 2] : [(l.minX + l.maxX) / 2, l.minZ + tip];
        ctx.moveTo(px(p0[0]!), py(p0[1]!));
        ctx.lineTo(px(p1[0]!), py(p1[1]!));
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  // Checkpoint lines (dashed white) and the chequered start / finish line.
  ctx.strokeStyle = "rgba(255,255,255,0.75)";
  ctx.lineWidth = 0.3 * sx;
  ctx.setLineDash([1.2 * sx, 0.8 * sx]);
  for (const cp of TRUCK_CHECKPOINTS) {
    if (cp.index === 0) continue;
    ctx.beginPath();
    ctx.moveTo(px(cp.a.x), py(cp.a.z));
    ctx.lineTo(px(cp.b.x), py(cp.b.z));
    ctx.stroke();
  }
  ctx.setLineDash([]);
  const cell = 0.8;
  for (let zi = 0; zi < Math.round((TRACK.island.minZ - PIT.trackEdgeZ) / cell); zi++) {
    for (let xi = 0; xi < 2; xi++) {
      ctx.fillStyle = (zi + xi) % 2 === 0 ? "#F7F7F2" : "#1C1C1C";
      ctx.fillRect(px(START_LINE_X - cell + xi * cell), py(PIT.trackEdgeZ + (zi + 1) * cell), cell * sx + 0.5, cell * sz + 0.5);
    }
  }
  // Starting grid: white boxes with their numbers.
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 0.22 * sx;
  ctx.font = `bold ${Math.round(1.6 * sz)}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const s of TRUCK_GRID) {
    const b = s.rect;
    ctx.strokeRect(px(b.minX + 0.3), py(b.maxZ - 0.6), (b.maxX - b.minX - 0.6) * sx, (b.maxZ - b.minZ - 1.2) * sz);
    ctx.save();
    ctx.translate(px(b.minX + 1.2), py((b.minZ + b.maxZ) / 2));
    ctx.rotate(Math.PI / 2);
    ctx.fillText(String(s.index + 1), 0, 0);
    ctx.restore();
  }
  tex.update();
}
